import { FileSearch, LayoutDashboard, LogOut, Moon, Sun, UserRound } from "lucide-react";
import { useEffect } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { Logo } from "../../components/Logo";
import { PageHeaderProvider, usePageHeaderValue } from "../../components/shell/PageHeaderContext";
import { useHideOnScroll } from "../../components/shell/useHideOnScroll";
import { initials } from "../../lib/format";
import { useTheme } from "../../theme/ThemeContext";

export const PARTNER_BASE = "/aliados";

const navigation = [
  { to: PARTNER_BASE, label: "Resumen", icon: LayoutDashboard, end: true },
  { to: `${PARTNER_BASE}/solicitudes`, label: "Solicitudes", icon: FileSearch },
  { to: `${PARTNER_BASE}/cuenta`, label: "Mi cuenta", icon: UserRound }
];

/**
 * The lending partner's portal frame. Same visual shell as the customer app (sidebar, sticky
 * topbar, bottom tabs on phones) with the partner's own navigation: a reviewer never sees wallets,
 * destinations or the assistant.
 */
export function PartnerShell() {
  return (
    <PageHeaderProvider>
      <PartnerFrame />
    </PageHeaderProvider>
  );
}

function PartnerFrame() {
  const { user, logout } = useAuth();
  const header = usePageHeaderValue();
  const { pathname } = useLocation();
  const navHidden = useHideOnScroll(pathname);
  const { resolved, toggle } = useTheme();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  return (
    <div className="app-shell partner-shell">
      <aside className="side-nav" aria-label="Portal de aliados">
        <div className="side-brand partner-brand">
          <Logo height={24} />
          <span className="partner-brand-tag">Portal de aliados</span>
        </div>
        <nav>
          <span className="nav-group">Peso a Peso</span>
          {navigation.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}>
              <Icon aria-hidden="true" size={18} />
              <span>{label}</span>
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <>
              <span className="nav-group">Aventuria</span>
              <NavLink to="/admin" className="nav-item"><LayoutDashboard aria-hidden="true" size={18} /><span>Volver a administración</span></NavLink>
            </>
          )}
        </nav>
        <div className="side-account">
          <NavLink to={`${PARTNER_BASE}/cuenta`} className="side-account-link" aria-label="Mi cuenta">
            <div className="avatar" aria-hidden="true">{initials(user?.fullName)}</div>
            <div className="account-copy"><strong>{user?.fullName}</strong><span>{user?.role === "admin" ? "Administración Aventuria" : "Revisor · Peso a Peso"}</span></div>
          </NavLink>
          <button className="icon-button ghost" onClick={() => void logout()} aria-label="Cerrar sesión" title="Cerrar sesión">
            <LogOut size={17} aria-hidden="true" />
          </button>
        </div>
      </aside>

      <div className="app-content">
        <header className="topbar">
          <div className="topbar-copy">
            <h1>{header.title}</h1>
            {header.subtitle && <span className="topbar-subtitle">{header.subtitle}</span>}
          </div>
          <div className="topbar-actions">
            {header.actions}
            <button type="button" className="topbar-button" onClick={toggle} aria-label={resolved === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"} aria-pressed={resolved === "dark"}>
              {resolved === "dark" ? <Sun size={17} aria-hidden="true" /> : <Moon size={17} aria-hidden="true" />}
            </button>
          </div>
        </header>
        <main className="app-main"><Outlet /></main>
      </div>

      <nav className={`bottom-nav${navHidden ? " is-hidden" : ""}`} aria-label="Portal de aliados">
        {navigation.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} className={({ isActive }) => `bottom-nav-item${isActive ? " active" : ""}`}>
            <Icon aria-hidden="true" size={22} />
            <span>{label}</span>
          </NavLink>
        ))}
        <button type="button" className="bottom-nav-item" onClick={() => void logout()}>
          <LogOut aria-hidden="true" size={22} />
          <span>Salir</span>
        </button>
      </nav>
    </div>
  );
}
